import { NavLink, useLocation } from 'react-router-dom';
import {
  IconExpand,
  Table,
  Popup,
  PermissionEnum,
  SchemaFilterType,
  IconAlternativeSettings,
  SortDir,
  Filters,
} from '@ui/src';
import { ReportTableContentTypes } from '../types';
import { columns, columnsLabels, expandableColumns } from '../variables/eftr';
import AddTransactionsSidepanel from './add-transactions-sidepanel';

import ROUTES from '../../../routes';

import cn from '../styles.module.css';

const fintracStatuses = [
  {
    name: 'Created',
    value: 'CREATED',
  },
  {
    name: 'Sent',
    value: 'SENT',
  },
  {
    name: 'Success',
    value: 'SUCCESS',
  },
  {
    name: 'Rejected',
    value: 'REJECTED',
  },
  {
    name: 'Canceled',
    value: 'CANCELLED',
  },
  {
    name: 'All',
    value: 'ALL',
  },
];

const ReportTableContent = ({
  formattedData,
  onExpand,
  pagination,
  type,
  statusFilter,
  setStatusFilter,
  cancelReportVisible,
  setCancelReportVisible,
  submitReportVisible,
  setSubmitReportVisible,
  selectedReportAmount,
  addTransactionsSidepanelVisible,
  setAddTransactionsSidepanelVisible,
  selectedReportTransferDate,
  setSelectedReportTransferDate,
  activePermissions,
  isLoading,
  isFetching,
  setPage,
  onSort,
  errorList,
  sort,
  filtersComponent,
  handleSubmit,
  filters,
  onFilter,
  selectedReportId,
  deletePopupVisible,
  setDeletePopupVisible,
  setDeleteReportId,
  deleteError,
  deleteInProgress,
  onDeleteReport,
  onSubmitReport,
  submitInProgress,
  onCancelReport,
  cancelInProgress,
  setDeleteError,
}: ReportTableContentTypes) => {
  const { search } = useLocation();

  return (
    <>
      <div className={cn.fintracReportsTop}>
        <div
          className={`${cn.fintracControls} ${
            !activePermissions?.includes(
              PermissionEnum.REGULATORY_REPORT_FINTRAC_CREATE_ACTION,
            )
              ? cn.flexEnd
              : ''
          }`}
        >
          {activePermissions?.includes(
            PermissionEnum.REGULATORY_REPORT_FINTRAC_CREATE_ACTION,
          ) && (
            <NavLink
              to={`${type === 'eftr' ? ROUTES.FINTRAC_EFTR_CREATE : ROUTES.FINTRAC_LVCTR_CREATE}${search || ''}`}
              className={cn.createReportLink}
            >
              <IconExpand /> Create report
            </NavLink>
          )}
          {activePermissions?.includes(
            PermissionEnum.SETTINGS_REGULATORY_REPORT_FINTRAC_EDIT_ACTION,
          ) && (
            <NavLink
              to="/settings/reports/fintrac"
              className={cn.reportSettingsButton}
              target="_blank"
            >
              <IconAlternativeSettings width={16} height={16} />
              Settings
            </NavLink>
          )}
        </div>
        <div className={cn.fintracStatuses}>
          {fintracStatuses?.map((item) => (
            <button
              type="button"
              key={item.value}
              className={`${cn.fintracStatusButton} ${statusFilter === item.value ? cn.active : ''}`}
              onClick={() => {
                setStatusFilter(statusFilter === item.value ? '' : item.value);

                const nextFilters: Filters = {
                  ...filters,
                  sortProp: {
                    value: ['createdAt'],
                    type: SchemaFilterType.STRING,
                  },
                  sortDir: {
                    value: [SortDir.DESC],
                    type: SchemaFilterType.STRING,
                  },
                };

                nextFilters.reportType = {
                  value: [type === 'eftr' ? 'EFTR' : 'LVCTR'],
                  type: SchemaFilterType.STRING,
                };

                if (item.value !== 'ALL' && statusFilter !== item.value) {
                  nextFilters.status = {
                    value: [item.value],
                    type: SchemaFilterType.STRING,
                  };
                } else if (
                  item.value === 'ALL' ||
                  statusFilter === item.value
                ) {
                  delete nextFilters.status;
                }

                onFilter(nextFilters);
              }}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>
      <Table
        columns={columns}
        data={formattedData || []}
        onSort={onSort}
        filters={{}}
        sort={sort}
        columnsLabels={columnsLabels}
        onFilterChange={() => {}}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        error={errorList}
        tableFormClassName={cn.fintracReportsList}
        take="30"
        setPage={(data) => {
          const nextFilters = { ...filters };

          nextFilters.reportType = {
            value: [type === 'eftr' ? 'EFTR' : 'LVCTR'],
            type: SchemaFilterType.STRING,
          };

          setPage({
            ...data,
            filters: nextFilters,
          });
        }}
        expandableColumns={expandableColumns}
        onExpand={onExpand}
        expandableColspanValue={8}
        pagination={pagination}
        tableTopClassName={cn.eftrReportTableTop}
        isLoading={isLoading || isFetching}
        expandable
        withExpandableContent
        withVerticalScroll
        withScroll
      />
      <AddTransactionsSidepanel
        setAddTransactionsSidepanelVisible={setAddTransactionsSidepanelVisible}
        selectedReportTransferDate={selectedReportTransferDate}
        setSelectedReportTransferDate={setSelectedReportTransferDate}
        addTransactionsSidepanelVisible={addTransactionsSidepanelVisible}
        type={type}
        selectedReportId={selectedReportId}
      />
      <Popup
        isVisible={cancelReportVisible}
        onCancelClick={() => {
          setCancelReportVisible(false);
          setDeleteError('');
        }}
        onOkClick={onCancelReport}
        title="Cancel Report?"
        description="This report will not be submitted to FINTRAC. The transactions will be retained in the system."
        okButtonText={cancelInProgress ? 'Loading...' : 'Cancel report'}
        okButtonDisabled={cancelInProgress}
        cancelButtonText="Keep report"
      >
        <div>
          {deleteError && (
            <div className={cn.deleteReportError}>{deleteError}</div>
          )}
        </div>
      </Popup>
      <Popup
        isVisible={submitReportVisible}
        onCancelClick={() => {
          setSubmitReportVisible(false);
          setDeleteError('');
        }}
        onOkClick={onSubmitReport}
        title="Submit report?"
        description={`This report with total amount of ${selectedReportAmount} CAD will be submitted. Once sent, the report cannot be edited.`}
        okButtonText={submitInProgress ? 'Loading...' : 'Submit report'}
        okButtonDisabled={submitInProgress}
        cancelButtonText="Cancel"
      >
        <div>
          {deleteError && (
            <div className={cn.deleteReportError}>{deleteError}</div>
          )}
        </div>
      </Popup>
      <Popup
        isVisible={deletePopupVisible}
        onCancelClick={() => {
          setDeletePopupVisible(false);
          setDeleteReportId('');
          setDeleteError('');
        }}
        onOkClick={onDeleteReport}
        title="Are you sure you want to delete this report?"
        okButtonText={deleteInProgress ? 'Loading...' : 'Yes, Delete'}
        cancelButtonText="Cancel"
        okButtonDisabled={deleteInProgress}
      >
        <div>
          {deleteError && (
            <div className={cn.deleteReportError}>{deleteError}</div>
          )}
        </div>
      </Popup>
    </>
  );
};

export default ReportTableContent;
